import { FC, useCallback } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography } from '@altermaind/web/ui';
import { Charts } from '@/_shared/components/Charts';
import { ReadMoreTypography } from '@/_shared/components/ReadMoreTypography';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getTemplatesCall,
} from '@/surveys/state/api';

type Props = {
  currentTemplateId?: string;
};

export const RecapTeamResults: FC<Props> = ({ currentTemplateId }) => {
  const t = useTranslations('departments.tabs.team.assessment.recap');
  const { proElementsTeamStats, conElementsTeamStats, teamStats } =
    getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { genericResponsiveValue } = useResponsiveStyle();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  return (
    <>
      <Typography variant='h2' mb='xs'>
        {t('title', {
          templateName:
            responseGetTemplates?.find(template => template.id === currentTemplateId)?.name || '',
        })}
      </Typography>
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        mb='l'
      >
        <Box
          flex='2'
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 's',
          })}
        >
          <Typography variant='p3/light' mb='l'>
            {t('description', {
              metricsCount: Array.isArray(teamStats?.metricsScore)
                ? teamStats.metricsScore.length
                : 0,
              templateName:
                responseGetTemplates?.find(template => template.id === currentTemplateId)?.name ||
                '',
            })}
          </Typography>
          <Charts.RadarChartComponent
            data={
              Array.isArray(teamStats?.metricsScore)
                ? teamStats.metricsScore.map(item => ({
                    subject: item.metric?.name ?? '',
                    current: item.score ?? 0,
                    expected: 7,
                    description: getDescriptionMetric(
                      currentTemplateId || '',
                      item.metric?.name || '',
                    ),
                  }))
                : []
            }
          />
        </Box>
        <Box flex='1'>
          <Card>
            {teamStats?.insight && (
              <>
                <Typography variant='h4'>{t('insignBox.title')}</Typography>
                <Box display='flex' flexDirection='column' mb='m' mt='xs'>
                  <ReadMoreTypography variant='p3/light'>{teamStats?.insight}</ReadMoreTypography>
                </Box>
              </>
            )}
            <Typography variant='h4'>{t('insignBox.proTitle')}</Typography>
            <Box display='flex' flexDirection='column' mb='m' mt='xs'>
              {proElementsTeamStats?.map(item => (
                <Card
                  mb='xxs'
                  backgroundColor='primary/inactive'
                  key={item.metric?.name}
                  px='xs'
                  py='xxxs'
                  width='fit-content'
                >
                  <Typography variant='p2/light' fontSize='xs'>
                    {item.metric?.name}
                  </Typography>
                </Card>
              ))}
            </Box>
            <Typography variant='h4'>{t('insignBox.conTitle')}</Typography>
            <Box display='flex' flexDirection='column' mt='xs'>
              {conElementsTeamStats?.map(item => (
                <Card
                  mb='xxs'
                  backgroundColor='error/opacity'
                  key={item.metric?.name}
                  px='xs'
                  py='xxxs'
                  width='fit-content'
                >
                  <Typography variant='p2/light' fontSize='xs'>
                    {item.metric?.name}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};
